// src/admin/CatechistStatusManager.js - Đã loại bỏ cột Giới Tính
import React, { useState, useContext, useMemo } from 'react';
import { StudentContext } from '../StudentContext';
import { updateCatechist, resetCatechistPassword, deleteCatechist } from '../FirebaseService';
import './CatechistStatusManager.css';

const CatechistStatusManager = () => {
  const { catechistsData, updateLocalCatechist, deleteLocalCatechist } = useContext(StudentContext);

  const [isUpdating, setIsUpdating] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const [deletedUids, setDeletedUids] = useState(new Set());
  const currentAdminUid = localStorage.getItem('uid');

  // State Modal Reset Password
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [targetCatechist, setTargetCatechist] = useState(null);
  const [newPassword, setNewPassword] = useState('Annhon@1965');
  const [isResetting, setIsResetting] = useState(false);

  // State Modal Xóa Tài Khoản
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingCatechist, setDeletingCatechist] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Đang hoạt động' },
    { value: 'inactive', label: 'Không còn hoạt động' },
  ];

  const roleOptions = [
    { value: 'catechist', label: 'Giáo lý viên' },
    { value: 'admin', label: 'Admin' },
  ];

  const handleUpdateField = async (uid, field, value) => {
    setIsUpdating((prev) => ({ ...prev, [`${uid}-${field}`]: true }));
    try {
      const updates = { [field]: value };
      const response = await updateCatechist(uid, updates);
      if (response.success) {
        updateLocalCatechist(uid, updates);
      } else {
        alert(response.message || 'Lỗi khi cập nhật.');
      }
    } catch (error) {
      console.error(`Error updating catechist ${field}:`, error);
      alert(error.message || 'Lỗi khi cập nhật. Vui lòng thử lại.');
    } finally {
      setIsUpdating((prev) => ({ ...prev, [`${uid}-${field}`]: false }));
    }
  };

  const handleOpenResetModal = (catechist) => {
    setTargetCatechist(catechist);
    setNewPassword('Annhon@1965');
    setIsResetModalOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!targetCatechist || !newPassword.trim()) {
      alert('Vui lòng nhập mật khẩu mới!');
      return;
    }

    setIsResetting(true);
    try {
      const res = await resetCatechistPassword(targetCatechist.uid, newPassword.trim());
      if (res.success) {
        alert(`🎉 Đã đặt lại mật khẩu cho Giáo lý viên ${targetCatechist.fullName} thành công!`);
        updateLocalCatechist(targetCatechist.uid, {
          passwordChangeCount: 0,
          lastPasswordChange: new Date()
        });
        setIsResetModalOpen(false);
        setTargetCatechist(null);
      } else {
        alert('❌ ' + (res.message || 'Không thể đặt lại mật khẩu.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenDeleteModal = (catechist) => {
    if (catechist.uid === currentAdminUid) {
      alert('⚠️ Bạn không thể xóa tài khoản của chính mình!');
      return;
    }
    setDeletingCatechist(catechist);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCatechist) return;

    setIsDeleting(true);
    try {
      const res = await deleteCatechist(deletingCatechist.uid);
      if (res.success) {
        alert(`🗑️ Đã xóa vĩnh viễn tài khoản của ${deletingCatechist.fullName}!`);
        if (typeof deleteLocalCatechist === 'function') {
          deleteLocalCatechist(deletingCatechist.uid);
        }
        setDeletedUids((prev) => new Set(prev).add(deletingCatechist.uid));
        setIsDeleteModalOpen(false);
        setDeletingCatechist(null);
      } else {
        alert('❌ ' + (res.message || 'Lỗi khi xóa tài khoản.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCatechists = useMemo(() => {
    if (!catechistsData || catechistsData.length === 0) return [];
    const searchLower = searchTerm.toLowerCase();
    return catechistsData.filter((catechist) => {
      if (deletedUids.has(catechist.uid)) return false;
      return (
        (catechist.idCode && catechist.idCode.toLowerCase().includes(searchLower)) ||
        (catechist.holyName && catechist.holyName.toLowerCase().includes(searchLower)) ||
        (catechist.fullName && catechist.fullName.toLowerCase().includes(searchLower)) ||
        (catechist.username && catechist.username.toLowerCase().includes(searchLower)) ||
        (catechist.role && catechist.role.toLowerCase().includes(searchLower))
      );
    });
  }, [catechistsData, searchTerm, deletedUids]);

  const sortedCatechists = useMemo(() => {
    if (!sortColumn) return filteredCatechists;

    const getLastName = (name) => {
      if (!name) return '';
      const parts = name.trim().split(/\s+/);
      return parts[parts.length - 1].toLowerCase();
    };

    return [...filteredCatechists].sort((a, b) => {
      let valA;
      let valB;

      if (sortColumn === 'idCode') {
        return sortDirection === 'asc'
          ? String(a.idCode || '').localeCompare(String(b.idCode || ''), undefined, { numeric: true, sensitivity: 'base' })
          : String(b.idCode || '').localeCompare(String(a.idCode || ''), undefined, { numeric: true, sensitivity: 'base' });
      }

      if (sortColumn === 'fullName') {
        valA = getLastName(a.fullName);
        valB = getLastName(b.fullName);
      } else if (sortColumn === 'lastPasswordChange') {
        valA = a.lastPasswordChange ? a.lastPasswordChange.seconds : 0;
        valB = b.lastPasswordChange ? b.lastPasswordChange.seconds : 0;
      } else if (sortColumn === 'passwordChangeCount') {
        valA = a.passwordChangeCount || 0;
        valB = b.passwordChangeCount || 0;
      } else {
        valA = a[sortColumn] || '';
        valB = b[sortColumn] || '';
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCatechists, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Chưa đổi';
    const date = typeof timestamp.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div className="csm-container">
      <div className="csm-header">
        <h2 className="csm-title">Quản lý Trạng thái, Quyền & Mật khẩu Giáo Lý Viên</h2>
        <input
          type="text"
          placeholder="Tìm kiếm giáo lý viên..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="csm-search-input"
        />
      </div>

      <div className="csm-table-wrapper">
        <table className="csm-table">
          <thead>
            <tr>
              <th className="csm-th" onClick={() => handleSort('idCode')}>
                ID Code{getSortIcon('idCode')}
              </th>
              <th className="csm-th" onClick={() => handleSort('holyName')}>
                Tên Thánh{getSortIcon('holyName')}
              </th>
              <th className="csm-th csm-sticky-col-head" onClick={() => handleSort('fullName')}>
                Họ và Tên{getSortIcon('fullName')}
              </th>
              <th className="csm-th" onClick={() => handleSort('username')}>
                Username{getSortIcon('username')}
              </th>
              <th className="csm-th csm-text-center" onClick={() => handleSort('role')}>
                Quyền{getSortIcon('role')}
              </th>
              <th className="csm-th" onClick={() => handleSort('status')}>
                Trạng thái{getSortIcon('status')}
              </th>
              <th className="csm-th csm-text-center" onClick={() => handleSort('passwordChangeCount')}>
                Số lần đổi MK{getSortIcon('passwordChangeCount')}
              </th>
              <th className="csm-th csm-text-center" onClick={() => handleSort('lastPasswordChange')}>
                Lần cuối đổi MK{getSortIcon('lastPasswordChange')}
              </th>
              <th className="csm-th csm-text-center">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedCatechists.length > 0 ? (
              sortedCatechists.map((catechist, index) => {
                const isSelf = catechist.uid === currentAdminUid;
                return (
                  <tr key={catechist.uid || `glv-status-${index}-${catechist.username}`}>
                    <td className="csm-td csm-id-code">{catechist.idCode}</td>
                    <td className="csm-td">{catechist.holyName || '-'}</td>
                    <td className="csm-td csm-sticky-col">{catechist.fullName}</td>
                    <td className="csm-td">{catechist.username}</td>

                    {/* DROPDOWN QUYỀN */}
                    <td className="csm-td csm-text-center">
                      <select
                        value={catechist.role || 'catechist'}
                        onChange={(e) => handleUpdateField(catechist.uid, 'role', e.target.value)}
                        disabled={isSelf || isUpdating[`${catechist.uid}-role`]}
                        className="csm-select"
                        title={isSelf ? 'Không thể tự hạ quyền của chính mình' : ''}
                      >
                        {roleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>

                    <td className="csm-td">
                      <select
                        value={catechist.status || 'active'}
                        onChange={(e) => handleUpdateField(catechist.uid, 'status', e.target.value)}
                        disabled={isUpdating[`${catechist.uid}-status`]}
                        className="csm-select"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="csm-td csm-text-center">{catechist.passwordChangeCount || 0}</td>
                    <td className="csm-td csm-text-center">{formatDate(catechist.lastPasswordChange)}</td>
                    <td className="csm-td csm-text-center">
                      <div className="csm-actions-group">
                        <button
                          onClick={() => handleOpenResetModal(catechist)}
                          className="csm-btn-reset"
                          title="Đặt lại mật khẩu"
                        >
                          🔑 Reset
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(catechist)}
                          className="csm-btn-delete"
                          disabled={isSelf}
                          title={isSelf ? 'Không thể xóa chính mình' : 'Xóa tài khoản'}
                        >
                          🗑️ Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="csm-td csm-text-center">
                  {searchTerm ? 'Không tìm thấy giáo lý viên nào phù hợp.' : 'Không có dữ liệu giáo lý viên hoặc chưa tải xong.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL RESET MẬT KHẨU */}
      {isResetModalOpen && targetCatechist && (
        <div className="csm-modal-overlay">
          <div className="csm-modal-card">
            <h3 className="csm-modal-title">🔑 Reset Mật Khẩu Giáo Lý Viên</h3>
            
            <div className="csm-modal-info">
              <p><strong>Tên Thánh:</strong> {targetCatechist.holyName || 'Chưa có'}</p>
              <p><strong>Họ và Tên:</strong> {targetCatechist.fullName}</p>
              <p><strong>Username:</strong> <code className="csm-code">{targetCatechist.username}</code></p>
            </div>

            <div className="csm-modal-field">
              <label className="csm-label">Mật khẩu mới:</label>
              <div className="csm-input-group">
                <input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="csm-input"
                  placeholder="Nhập mật khẩu mới..."
                />
                <button
                  type="button"
                  onClick={() => setNewPassword('Annhon@1965')}
                  className="csm-btn-default"
                >
                  Mặc định
                </button>
              </div>
            </div>

            <div className="csm-modal-actions">
              <button
                onClick={() => setIsResetModalOpen(false)}
                disabled={isResetting}
                className="csm-btn-cancel"
              >
                Hủy Bỏ
              </button>
              <button
                onClick={handleConfirmResetPassword}
                disabled={isResetting}
                className="csm-btn-confirm"
              >
                {isResetting ? '⏳ Đang xử lý...' : '🚀 Xác Nhận Reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL XÁC NHẬN XÓA TÀI KHOẢN */}
      {isDeleteModalOpen && deletingCatechist && (
        <div className="csm-modal-overlay">
          <div className="csm-modal-card">
            <h3 className="csm-modal-title" style={{ color: '#b91c1c' }}>⚠️ Xóa Tài Khoản Giáo Lý Viên</h3>
            
            <div className="csm-modal-info">
              <p><strong>Tên Thánh & Họ Tên:</strong> {deletingCatechist.holyName ? `${deletingCatechist.holyName} ` : ''}{deletingCatechist.fullName}</p>
              <p><strong>Username:</strong> <code className="csm-code">{deletingCatechist.username}</code></p>
              <p><strong>Mã ID:</strong> {deletingCatechist.idCode}</p>
            </div>

            <div className="csm-delete-warning">
              <strong>Lưu ý quan trọng:</strong> Thao tác này sẽ xóa vĩnh viễn tài khoản đăng nhập trên hệ thống và hồ sơ trong cơ sở dữ liệu. Thao tác này <strong>không thể hoàn tác</strong>.
            </div>

            <div className="csm-modal-actions">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
                className="csm-btn-cancel"
              >
                Hủy Bỏ
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="csm-btn-confirm-delete"
              >
                {isDeleting ? '⏳ Đang xóa...' : '🗑️ Xác Nhận Xóa Vĩnh Viễn'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatechistStatusManager;